import { useState, useRef, useEffect, useLayoutEffect, useMemo, Suspense } from "react";
import { createPortal } from "react-dom";
import { Home as HomeIcon, DollarSign, Settings, LogOut, Bell, Handshake, WifiOff, Route, Star, CalendarClock, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from 'motion/react';
import { TripConfig, Expense, Payment, Memory, Day, PendingImport } from "./types";
import { AuthScreen } from "./components/AuthScreen";
import { UserAvatar } from "./components/UserAvatar";
import { SettingsModal } from "./components/SettingsModal";
import { NotificationCenter } from "./components/NotificationCenter";
import { ModernCompass } from "./components/ModernCompass";
import { TalismanLogo } from "./components/Icons";
import { ImportBookingModal, ExpensesTab, LinkedExpenseItem, MemoriesTab } from "./components/LazyModals";
import { HomeTab } from "./components/Home";
import { ItineraryTab, TAB_INTRO_COPY } from "./components/Itinerary";
import { TRIP_CONFIG as INITIAL_TRIP_CONFIG } from './data';
import { Card, CardTintStreaks, EmptyState, IntroGate } from "./components/UI";
import { useAuth } from "./utils/AuthContext";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import { useLocalDate } from "./hooks/useLocalDate";
import { doc, collection, query, where, orderBy, setDoc, updateDoc, deleteDoc, getDocs, serverTimestamp, writeBatch, arrayUnion } from "firebase/firestore";
import { db, auth } from "./services/firebase";
import { configBaseline, writeTripConfig } from './utils/tripConfigWrites';
import { changeTripRoute } from './services/tripRoute';
import { stripUndef } from './utils/dayWrites';
import { deleteTripFully } from './utils/accountDeletion';
import { liveQuery, reconnectOnForeground, type LiveQueryHandle } from "./utils/liveQuery";
import { Notification } from "./types";
import { getCurrencyFromLocation } from "./utils/currencyUtils";
import { getFirstName, getTripStatus, nextActiveTripId } from "./utils/tripUtils";
import { initPush } from "./services/pushService";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { publicTripTraveller, useProfiles } from "./utils/ProfilesContext";
import { TAB_LABELS } from "./constants/tabs";
import { userMessage } from './utils/userMessage';
import { withDestinationIds } from './utils/destinationItems';
import { openDeepLink } from './utils/deepLinks';
import { isTripAdmin } from './utils/tripRoles';
import { useTripBranches } from './hooks/useTripBranches';
import { JunctionContext, junctionsByScope, personalHomeRoute, PersonalRouteContext } from './utils/itineraryBranches';
import { accessibleExpenseScopes, branchExpenseSummary } from './utils/branchExpenseSummary';
import { waitForBrandFonts } from './utils/startup';

const TABS = [
  { id: "home", icon: <HomeIcon className="w-5 h-5" />, label: TAB_LABELS.home },
  { id: "logistics", icon: <Route className="w-5 h-5" />, label: TAB_LABELS.logistics },
  { id: "bucketlist", icon: <Star className="w-5 h-5" />, label: TAB_LABELS.bucketlist },
  { id: "dayplanner", icon: <CalendarClock className="w-5 h-5" />, label: TAB_LABELS.dayplanner },
  { id: "expenses", icon: <DollarSign className="w-5 h-5" />, label: TAB_LABELS.expenses },
] as const;

// "memories" is no longer a nav tab but stays reachable via notification
// deep-links (eg. "X liked your memory") until Memories finds a new home
// in the post-trip experience — see BACKLOG.md.
type TabId = typeof TABS[number]["id"] | "memories";

// Trip to open a pending (forwarded-email) import against. `suggestedTripId`
// is only set server-side when a booking date actually fell inside a trip's
// date range — a real match, not a guess. When it's absent (or that trip no
// longer exists), fall back to the active trip just so the review modal has
// something to initialize with, but report `confirmed: false` so the modal
// holds for the user to explicitly pick a trip rather than silently saving
// into whichever trip happened to be open.
function resolvePendingImportTrip(
  trips: TripConfig[],
  suggestedTripId: string | null | undefined,
  activeTrip: TripConfig | null,
): { tripConfig: TripConfig | null; confirmed: boolean } {
  const suggested = suggestedTripId ? trips.find(t => t.id === suggestedTripId) : undefined;
  return { tripConfig: suggested ?? activeTrip ?? null, confirmed: !!suggested };
}

export default function App({ resumingUpdate = false }: { resumingUpdate?: boolean }) {
  const today = useLocalDate();
  const { user: currentUser, firebaseUser, loading: authLoading, signOut } = useAuth();
  const [openingSplashElapsed, setOpeningSplashElapsed] = useState(resumingUpdate);
  const [brandFonts, setBrandFonts] = useState<'pending' | 'ready' | 'fallback'>('pending');
  useEffect(() => {
    if (resumingUpdate) return;
    const timer = window.setTimeout(() => setOpeningSplashElapsed(true), 2000);
    return () => window.clearTimeout(timer);
  }, [resumingUpdate]);
  useEffect(() => {
    if (resumingUpdate) return;
    let active = true;
    void waitForBrandFonts(document.fonts).then(ready => { if (active) setBrandFonts(ready ? 'ready' : 'fallback'); });
    return () => { active = false; };
  }, [resumingUpdate]);
  const { subscribeToIds, setTripProfiles } = useProfiles();
  const isOnline = useOnlineStatus();
  const [tab, setTab] = useState<TabId>("home");
  const [linkedExpenseId, setLinkedExpenseId] = useState<string | null>(null);
  const [linkedExpenseEditId, setLinkedExpenseEditId] = useState<string | null>(null);
  // Bumped by ShootingStarLayer, per nav tab id, when a comet flight lands —
  // only ever used as that tab icon's remount `key`, to replay a little
  // spring "pop" on arrival.
  const [navArrivalPulse, setNavArrivalPulse] = useState<Record<string, number>>({});
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const [triggerManage, setTriggerManage] = useState(false);

  const isFullyOnboarded = currentUser && currentUser.onboardingCompleted && firebaseUser?.emailVerified;

  // Close settings when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettings(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setShowSettings(false);
  }, []);

  // Close settings dropdown whenever auth completes (transition to logged-in state)
  useEffect(() => {
    if (isFullyOnboarded) {
      setShowSettings(false);
    }
  }, [isFullyOnboarded]);

  // Register FCM push token once the user is fully onboarded.
  // Use currentUser.id (string) not currentUser (object) — object refs change
  // on every Firestore snapshot and would re-trigger on every render.
  useEffect(() => {
    if (isFullyOnboarded && currentUser) {
      initPush(currentUser.id).catch(console.error);

      // Persist the device's IANA timezone so the daily itinerary digest can
      // fire at 8 am local time rather than a fixed UTC hour.
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      updateDoc(doc(db, 'users', currentUser.id), { timezone: tz }).catch(console.error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullyOnboarded, currentUser?.id]);

  const [trips, setTrips] = useState<TripConfig[]>([]);
  const [activeTripId, setActiveTripId] = useState<string>("");
  const [pendingTrip, setPendingTrip] = useState<TripConfig | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [pendingImports, setPendingImports] = useState<PendingImport[]>([]);
  const [pendingImportToOpen, setPendingImportToOpen] = useState<PendingImport | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [expensesTab, setExpensesTab] = useState<'log' | 'settle'>('log');

  // Shortlist and Schedule are separate nav tabs, but a couple of internal
  // flows (eg. clicking a day card in the Itinerary overview) ask to jump
  // into "day view" without naming which of the two — lastPlanTab remembers
  // whichever the user was on most recently, defaulting to Shortlist while
  // planning and flipping to Schedule once the trip is under way (the
  // day's schedule is what you want in front of you while travelling).
  // `planTabTouched` stops that default from yanking it back out from under
  // someone who has since picked the other one themselves.
  const [lastPlanTab, setLastPlanTab] = useState<'bucketlist' | 'dayplanner'>('bucketlist');
  const [planTabTouched, setPlanTabTouched] = useState(false);
  useEffect(() => {
    if (tab === 'bucketlist' || tab === 'dayplanner') {
      setPlanTabTouched(true);
      setLastPlanTab(tab);
    }
  }, [tab]);

  const itineraryView: 'overview' | 'day' = tab === 'logistics' ? 'overview' : 'day';
  const itineraryDetailMode: 'shortlists' | 'dayPlanner' = tab === 'dayplanner' ? 'dayPlanner' : 'shortlists';
  // Layout-relevant views: each needs its own <main> scroll/padding treatment
  // (see the className below).
  const isDayPlannerView = tab === 'dayplanner';
  const isBucketListView = tab === 'bucketlist';
  const handleItineraryViewChange = (v: 'overview' | 'day') => {
    if (v === 'overview') setTab('logistics');
    else setTab(lastPlanTab);
  };
  const handleItineraryDetailModeChange = (m: 'shortlists' | 'dayPlanner') => {
    setTab(m === 'dayPlanner' ? 'dayplanner' : 'bucketlist');
  };

  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [expenseReadyKey, setExpenseReadyKey] = useState('');
  // Explicit signal that tells ItineraryTab to open the creation form.
  // Set only when user clicks "Create Trip" — never by navigation alone.
  const [autoOpenCreate, setAutoOpenCreate] = useState(false);

  // App stays mounted across sign-out/sign-in. Clear the previous account's
  // selected tab and trip before another account can render them for a frame.
  useLayoutEffect(() => {
    if (isFullyOnboarded) return;
    setTab("home");
    setTrips([]);
    setActiveTripId("");
    setPendingTrip(null);
    setNotifications([]);
    setPendingImports([]);
    setPendingImportToOpen(null);
    setLinkedExpenseId(null);
    setHasAutoSelected(false);
    setPlanTabTouched(false);
    setAutoOpenCreate(false);
    setShowNotifications(false);
    setShowSettings(false);
    setShowSettingsModal(false);
  }, [isFullyOnboarded]);

  // Sync trips from Firestore
  useEffect(() => {
    if (!currentUser) return;

    let trips1: TripConfig[] = [];

    const updateTrips = () => {
      // Only show trips where the user is an accepted member (in memberIds).
      // Pending invitees (inviteEmails only) get a notification to accept but
      // must not see trip content until they do — that's when handleAcceptInvite
      // adds them to memberIds and q1 picks them up.
      // Preserve subcollection data already in state — the trips onSnapshot only
      // delivers the trip document itself, not its subcollections (expenses,
      // itinerary, memories, payments). Without this merge, any trip-document
      // change (eg. saving an accommodation) wipes those from local state and
      // they only come back on a full page reload.
      setTrips(prev => {
        const prevMap = new Map<string, TripConfig>(prev.map(t => [t.id, t]));
        return trips1.map(t => {
          const p = prevMap.get(t.id);
          if (!p) return t;
          return {
            ...t,
            ...(p.expenses  !== undefined && { expenses:  p.expenses  }),
            ...(p.payments  !== undefined && { payments:  p.payments  }),
            ...(p.memories  !== undefined && { memories:  p.memories  }),
            ...(p.itinerary !== undefined && { itinerary: p.itinerary }),
          };
        }).sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
      });
    };

    const q1 = query(collection(db, 'trips'), where('memberIds', 'array-contains', currentUser.id));

    // liveQuery, not a bare onSnapshot: these had no error handler at all, so
    // one dropped stream ended them permanently and silently — see
    // utils/liveQuery.ts for why that reads as "nothing saves until I
    // refresh".
    const tripsSub = liveQuery('trips', () => q1, (snapshot) => {
      trips1 = snapshot.docs.map(doc => doc.data() as TripConfig);
      updateTrips();
    });

    const notifQ = query(collection(db, 'users', currentUser.id, 'notifications'), orderBy('createdAt', 'desc'));
    const notifsSub = liveQuery('notifications', () => notifQ, (snapshot) => {
      setNotifications(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Notification));
    });

    const importsQ = query(
      collection(db, 'users', currentUser.id, 'pendingImports'),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc'),
    );
    const importsSub = liveQuery('pending imports', () => importsQ, (snapshot) => {
      setPendingImports(snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as PendingImport));
    });

    const all = [tripsSub, notifsSub, importsSub].filter(Boolean) as LiveQueryHandle[];
    const stopForegroundReconnect = reconnectOnForeground(all);

    return () => {
      stopForegroundReconnect();
      all.forEach(sub => sub.stop());
    };
  }, [currentUser]);

  // Sync active trip subcollections (expenses, memories, itinerary, payments)
  useEffect(() => {
    if (!currentUser || !activeTripId) return;

    const tripRef = doc(db, 'trips', activeTripId);


    // Same helper as the top-level listeners. The security-rules race these
    // four share — right after a trip is created the evaluator may not yet
    // see the trip document that isAcceptedMember's get() reads — is just the
    // fast end of liveQuery's backoff rather than a special case.
    const makeRetryListener = <T,>(
      collectionName: string,
      transform: (snapshot: import('firebase/firestore').QuerySnapshot) => T,
      setter: (data: T) => void,
      onReady?: (ready: boolean) => void,
    ) => liveQuery(collectionName, () => collection(tripRef, collectionName), snap => setter(transform(snap)), onReady);

    // 1. Expenses
    const expsSub = makeRetryListener(
      'expenses',
      (snap) => snap.docs.map(d => ({ ...d.data(), id: d.id }) as unknown as Expense),
      (expenses) => setTrips(prev => prev.map(t => t.id === activeTripId ? { ...t, expenses } : t)),
      ready => setExpenseReadyKey(ready ? `${currentUser.id}:${activeTripId}` : ''),
    );

    // 2. Payments
    const paymentsSub = makeRetryListener(
      'payments',
      (snap) => snap.docs.map(d => ({ ...d.data(), id: d.id }) as unknown as Payment),
      (payments) => setTrips(prev => prev.map(t => t.id === activeTripId ? { ...t, payments } : t)),
    );

    // 3. Memories
    const memoriesSub = makeRetryListener(
      'memories',
      (snap) => snap.docs.map(d => ({ ...d.data(), id: d.id }) as unknown as Memory),
      (memories) => setTrips(prev => prev.map(t => t.id === activeTripId ? { ...t, memories } : t)),
    );

    // 4. Itinerary
    const itinerarySub = makeRetryListener(
      'itinerary',
      (snap) => {
        const itinerary: Record<string, Partial<Day>> = {};
        snap.docs.forEach(d => { itinerary[d.id] = d.data() as Partial<Day>; });
        return itinerary;
      },
      (itinerary) => setTrips(prev => prev.map(t => t.id === activeTripId ? { ...t, itinerary } : t)),
    );

    const stopForeground = reconnectOnForeground([expsSub, paymentsSub, memoriesSub, itinerarySub]);

    return () => {
      stopForeground();
      expsSub.stop();
      paymentsSub.stop();
      memoriesSub.stop();
      itinerarySub.stop();
    };
  }, [currentUser, activeTripId]);

  useEffect(() => {
    if (trips.length > 0 && !activeTripId && !hasAutoSelected) {
      const activeTrips = trips.filter(t => getTripStatus(t, today) === 'active')
        .sort((a, b) => b.startDate.localeCompare(a.startDate));
      const upcomingTrips = trips.filter(t => getTripStatus(t, today) === 'countdown')
        .sort((a, b) => a.startDate.localeCompare(b.startDate));

      if (activeTrips.length > 0) {
        setActiveTripId(activeTrips[0].id);
      } else if (upcomingTrips.length > 0) {
        setActiveTripId(upcomingTrips[0].id);
      } else {
        // All trips are in the past — fall back to the most recent one.
        // trips is already sorted by startDate desc so trips[0] is the latest.
        setActiveTripId(trips[0].id);
      }
      setHasAutoSelected(true);
    }
  }, [trips, activeTripId, hasAutoSelected, today]);

  // Keep the selection pointing at a trip that still exists. `deleteTrip`
  // clears it when *you* delete one, but nothing did when a trip left your
  // list another way — another member removing you, the owner deleting it,
  // access revoked. The id then kept pointing at an unreadable trip while
  // `activeTrip` separately fell back to trips[0], so the app displayed one
  // trip while the expense/itinerary/memory listeners below subscribed to
  // another and failed with permission-denied: the visible trip's data never
  // loaded. See nextActiveTripId for the exact conditions.
  useEffect(() => {
    const next = nextActiveTripId(trips, activeTripId, !!pendingTrip);
    if (next) setActiveTripId(next);
  }, [trips, activeTripId, pendingTrip]);

  // Badge shows all unread notifications (any priority).
  // `read` boolean is the source of truth; terminal states (actioned/dismissed) are excluded.
  const unreadCount = notifications.filter(n => {
    const explicit = (n as any).state;
    if (explicit === 'actioned' || explicit === 'dismissed') return false;
    return !n.read;
  }).length;

  const activeTrip = pendingTrip || trips.find(t => t.id === activeTripId) || (trips.length > 0 ? trips[0] : null);
  const branchWorkspace = useTripBranches(pendingTrip ? null : activeTrip,currentUser?.id,config=>updateActiveTrip(config));
  const branchPersistence = branchWorkspace?.persistence;
  const expenseSummary = useMemo(() => activeTrip && !pendingTrip && currentUser ? branchExpenseSummary(
    activeTrip, branchWorkspace?.branches ?? [], branchPersistence?.configs ?? {}, currentUser.id,
    !branchPersistence || branchPersistence.loading || expenseReadyKey !== `${currentUser.id}:${activeTrip.id}`,
    branchPersistence?.error,
  ) : undefined, [activeTrip, pendingTrip, currentUser, branchWorkspace, branchPersistence, expenseReadyKey]);
  const homeRoute = useMemo(() => activeTrip && !pendingTrip && currentUser
    ? personalHomeRoute(activeTrip, branchWorkspace?.branches ?? [], branchPersistence?.configs ?? {}, currentUser.id)
    : undefined, [activeTrip, pendingTrip, currentUser, branchWorkspace, branchPersistence]);
  const junctions = useMemo(() => activeTrip && branchWorkspace
    ? junctionsByScope(activeTrip.id, branchWorkspace.branches, branchPersistence?.configs ?? {}) : {},
  [activeTrip, branchWorkspace, branchPersistence]);
  const expenseScopes = useMemo(() => activeTrip && currentUser
    ? accessibleExpenseScopes(activeTrip, branchWorkspace?.branches ?? [], branchPersistence?.configs ?? {}, currentUser.id)
    : [], [activeTrip, currentUser, branchWorkspace, branchPersistence]);
  const [linkedExpenseScope,setLinkedExpenseScope] = useState<string | undefined>();
  const linkedExpenseTrip = !linkedExpenseScope || linkedExpenseScope === activeTrip?.id ? activeTrip
    : Object.values(branchPersistence?.configs ?? {}).find(c=>c.id===linkedExpenseScope);
  const linkedExpense = linkedExpenseTrip?.expenses?.find(e => e.id === linkedExpenseId);
  const editingLinkedExpense = linkedExpenseTrip?.expenses?.find(e => e.id === linkedExpenseEditId);
  useEffect(() => { setLinkedExpenseId(null); setLinkedExpenseEditId(null); }, [activeTrip?.id]);
  useEffect(() => {
    const open = (event: Event) => {
      const detail=(event as CustomEvent<string | {id:string;scope?:string}>).detail;
      setLinkedExpenseId(typeof detail==='string'?detail:detail.id);
      setLinkedExpenseScope(typeof detail==='string'?undefined:detail.scope);
    };
    window.addEventListener('open-linked-expense', open);
    return () => window.removeEventListener('open-linked-expense', open);
  }, []);

  useEffect(() => {
    const edit = (event: Event) => {
      const detail=(event as CustomEvent<string | {id:string;scope?:string}>).detail;
      setLinkedExpenseEditId(typeof detail==='string'?detail:detail.id);
      setLinkedExpenseScope(typeof detail==='string'?undefined:detail.scope);
    };
    window.addEventListener('open-linked-expense-edit', edit);
    return () => window.removeEventListener('open-linked-expense-edit', edit);
  }, []);

  const activeTripStatus = activeTrip ? getTripStatus(activeTrip, today) : 'setup';
  useEffect(() => {
    if (planTabTouched) return;
    setLastPlanTab(activeTripStatus === 'active' ? 'dayplanner' : 'bucketlist');
  }, [activeTripStatus, planTabTouched]);

  // Trip config to use when reviewing a pending import — see resolvePendingImportTrip.
  const { tripConfig: pendingImportTripConfig, confirmed: pendingImportTripConfirmed } = pendingImportToOpen
    ? resolvePendingImportTrip(trips, pendingImportToOpen.suggestedTripId, activeTrip)
    : { tripConfig: null, confirmed: false };

  // Imports still waiting for review. A notification whose import was reviewed
  // since keeps its row but loses the "Review booking" button (it used to open
  // a browser alert saying it was already reviewed).
  const pendingImportIds = useMemo(() => new Set(pendingImports.map(p => p.id)), [pendingImports]);

  /** Called from NotificationCenter when the user clicks "Review booking" on an import. */
  const handleOpenImport = (importId: string) => {
    // The notification panel sits above the review (z-100 over z-50), so
    // leaving it open made "Review booking" look like it did nothing.
    setShowNotifications(false);
    const pi = pendingImports.find(p => p.id === importId);
    if (!pi) {
      alert('This booking has already been reviewed.');
      return;
    }
    if (!resolvePendingImportTrip(trips, pi.suggestedTripId, activeTrip).tripConfig) {
      alert('Create a trip first, then review this booking from your notifications.');
      return;
    }
    setPendingImportToOpen(pi);
    setTab('logistics');
  };

  /**
   * Mark a pending import as reviewed in Firestore. When a trip is assigned
   * (the user saved the import against a trip), stamp that tripId onto the
   * linked notification so it now filters under that specific trip.
   */
  const markImportReviewed = async (importId: string, tripId?: string) => {
    if (!currentUser) throw new Error('Sign in again to finish this import.');
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'users', currentUser.id, 'pendingImports', importId), {
        status: 'reviewed',
        reviewedAt: new Date().toISOString(),
      });
      if (tripId) {
        const notif = notifications.find(n => n.data?.pendingImportId === importId);
        if (notif) {
          batch.update(doc(db, 'users', currentUser.id, 'notifications', notif.id), {
            data: { ...notif.data, tripId },
          });
        }
      }
      await batch.commit();
    } catch (err) {
      console.error('markImportReviewed failed:', err);
      throw err;
    }
  };
  const hasTrip = activeTrip && activeTrip.title.trim() !== "";

  /**
   * The one-time intro card on Shortlist, Schedule and Expenses.
   *
   * Before the first trip exists these tabs have nothing to show, so they
   * already render an intro card whose CTA creates one. The card is just as
   * useful immediately AFTER that trip is created — it's the first time the
   * tab has ever been opened, and the interface behind it means nothing yet —
   * so it stays, with the CTA changed to "Get started", until dismissed once.
   *
   * Absence of the flag means "not yet seen", so this needs no backfill: any
   * traveller who has never dismissed a tab's card sees it on their next
   * visit, whether their trip is one minute or one month old.
   */
  const introSeen = currentUser?.introSeen ?? {};
  const dismissIntro = (key: 'bucketlist' | 'dayplanner' | 'expenses') => {
    if (!currentUser || introSeen[key]) return;
    updateDoc(doc(db, 'users', currentUser.id), { introSeen: { ...introSeen, [key]: true } })
      // Non-fatal: the card reappears next visit rather than blocking the tab.
      .catch(err => console.error('Could not record the intro as seen:', err));
  };

  /**
   * While it's up, the intro card IS the tab: one full-height card that brings
   * its own streaks. It therefore wants the ordinary padded, scrollable layout
   * every other card gets, not the tab's own header/scroller split — that
   * split drops both pt-4 and pb-[6.5rem], which is what left the card flush
   * to the header and running under the nav bar.
   */
  const showingIntro = !!hasTrip && (
    (tab === 'bucketlist' && !introSeen.bucketlist) ||
    (tab === 'dayplanner' && !introSeen.dayplanner) ||
    (tab === 'expenses' && !introSeen.expenses)
  );

  // Itinerary and Schedule hand their own inner scroller to <main> (see the
  // content area below) — but only once there's a trip to render a header and
  // a scrolling body for. With no trip, all five tabs render the same empty
  // card with neither, so they take the ordinary padded, scrollable layout
  // like everything else; otherwise those two sat higher than the rest and
  // ran under the nav bar, since this branch drops both pt-4 and pb-[6.5rem].
  const tabOwnsScroller = !!hasTrip && !showingIntro && (tab === 'logistics' || isDayPlannerView);

  /**
   * Itinerary, Shortlist and Schedule paint the drifting colour tint as their
   * page background rather than carrying it on a card. These three are the
   * planning surfaces — long, card-dense, scrolled for a while — and a tinted
   * page with translucent chrome floating on it gives them depth that a flat
   * bone background over solid white cards doesn't.
   *
   * What stays solid on them is content: a saved card, a destination card, an
   * Explorer CTA. What goes translucent is the invitation to add something —
   * the dashed CTAs — and the trip card at the top. The distinction is
   * roughly "a thing you have" versus "a space for a thing you don't".
   *
   * Not while the intro card or the no-trip empty state is up: both carry
   * their own streaks, and two drifting sets at once just reads as murk.
   */
  const tintedSurface = !!hasTrip && !showingIntro
    && (tab === 'logistics' || isDayPlannerView || isBucketListView);

  useEffect(() => {
    const visibleTrips = pendingTrip ? [...trips, pendingTrip] : trips;
    const acceptedIds = visibleTrips.flatMap(trip => [
      ...(trip.memberIds || []), ...(trip.adminIds || []), trip.ownerId,
    ]);
    subscribeToIds(acceptedIds);
    setTripProfiles(visibleTrips.flatMap(trip => trip.travellers || []), acceptedIds);
  }, [trips, pendingTrip, subscribeToIds, setTripProfiles]);

  /**
   * Put an admin back into memberIds when the trip document has lost them.
   *
   * adminIds and memberIds are maintained separately, and until the trip
   * write was made a merge, a save carrying a stale snapshot could drop whole
   * fields — the same data loss that was destroying inviteEmails. An admin
   * left out of memberIds could still see the trip but not write to it:
   * inviting a traveller came back as "Missing or insufficient permissions".
   *
   * The rules recognising adminIds is what unblocks the invite; this is what
   * repairs the document, so everything keyed on memberIds (the expense and
   * itinerary subcollections, the traveller profile subscription above) works
   * again too. Adding your own uid is explicitly permitted — it's the same
   * branch that lets an invitee accept.
   *
   * Only ever for someone the document already calls an admin or the owner.
   * A plain member dropped from memberIds can't be repaired from here and
   * can't be: the rules would refuse them, correctly, since nothing left in
   * the document says they belong.
   */
  const repairedTrips = useRef<Set<string>>(new Set());
  useEffect(() => {
    const uid = currentUser?.id;
    if (!activeTrip || !uid || repairedTrips.current.has(activeTrip.id)) return;
    if ((activeTrip.memberIds || []).includes(uid)) return;
    if (!(activeTrip.adminIds || []).includes(uid) && activeTrip.ownerId !== uid) return;
    repairedTrips.current.add(activeTrip.id);
    updateDoc(doc(db, 'trips', activeTrip.id), { memberIds: arrayUnion(uid) })
      // Non-fatal: the rules change alone keeps the trip usable.
      .catch(err => console.error('Could not restore trip membership:', err));
  }, [activeTrip, currentUser?.id]);

  // Accepts the pre-restructure tab names too ('trips'/'itinerary') — still
  // used by Notification.deepLink.tab on already-stored notifications, and by
  // NotificationCenter/Expenses' onNavigate props (typed as plain `string`,
  // since they just forward whatever a stored deep-link or caller passes) —
  // so any old value in flight still resolves to the right tab.
  const switchTab = (id: TabId | 'trips' | 'itinerary') => {
    // Legacy//internal ids → the tab that now hosts them.
    const resolved: TabId = id === 'trips' ? 'logistics' : id === 'itinerary' ? 'bucketlist' : id;
    // If navigating away from Logistics with an unsaved (no-title) pending trip,
    // discard it so saved trips remain accessible everywhere else.
    if (resolved !== 'logistics' && pendingTrip && pendingTrip.title.trim() === '') {
      setPendingTrip(null);
    }
    setTab(resolved);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  // ── Push notification deep linking ────────────────────────────────────────
  // All three refs/effects are here — AFTER trips, setActiveTripId, and switchTab
  // are declared, so none of those are in the temporal dead zone.
  const deepLinkHandlerRef = useRef<((p: Record<string, string>) => void) | null>(null);
  useEffect(() => {
    deepLinkHandlerRef.current = (params) => {
      const { tab, tripId, itemId, section, branchId } = params;
      if (!tab) return;
      // A private-branch push opens that branch; the workspace ignores a branch you can't open.
      if (tab === 'expenses' || branchId) branchPersistence?.select(branchId || null);
      if (tripId && trips.find(t => t.id === tripId)) setActiveTripId(tripId);
      switchTab(tab as TabId | 'trips' | 'itinerary');
      if (tab === 'expenses' && (section === 'settled-received' || section === 'settled-paid')) setExpensesTab('settle');
      // Same path as tapping it inside the app: opens the expense, memory or
      // itinerary item itself (the push path used to skip expenses and
      // memories), and survives the tab still loading.
      openDeepLink({ tab, itemId, section }, 400);
    };
  });

  // Cold open: app launched by tapping a push notification — read URL params once trips load
  const deepLinkConsumedRef = useRef(false);
  useEffect(() => {
    if (!isFullyOnboarded || trips.length === 0 || deepLinkConsumedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.get('tab')) return;
    deepLinkConsumedRef.current = true;
    window.history.replaceState({}, '', '/');
    deepLinkHandlerRef.current?.({
      tab:     params.get('tab')     ?? '',
      tripId:  params.get('tripId')  ?? '',
      dayDate: params.get('dayDate') ?? '',
      section: params.get('section') ?? '',
      itemId:  params.get('itemId')  ?? '',
      branchId: params.get('branchId') ?? '',
    });
  }, [isFullyOnboarded, trips]);

  // Invite email landing: ?invite=1 opens the notifications panel so the
  // pending invitee sees the Accept/Decline invite. Independent of trips
  // (a brand-new invitee has zero accepted trips, so the trip-gated deep-link
  // effect above never fires for them).
  const inviteParamConsumedRef = useRef(false);
  useEffect(() => {
    if (!isFullyOnboarded || inviteParamConsumedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('invite') !== '1') return;
    inviteParamConsumedRef.current = true;
    window.history.replaceState({}, '', '/');
    setShowNotifications(true);
  }, [isFullyOnboarded]);

  // Warm open: app already running — service worker sends a postMessage
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event: MessageEvent) => {
      if (event.data?.type !== 'PUSH_DEEP_LINK') return;
      deepLinkHandlerRef.current?.(event.data as Record<string, string>);
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  // Global tab-switch event — allows deep-linking from anywhere in the tree
  // without prop drilling. Used by itinerary "Cost logged to expenses" CTAs
  // to jump to the linked expense entry.
  const [importNavigation, setImportNavigation] = useState<{ tab: 'logistics' | 'bucketlist'; tripId: string; branchId?: string; itemId?: string } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.tripId && (detail.tab === 'logistics' || detail.tab === 'bucketlist')) {
        setImportNavigation(detail);
        setActiveTripId(detail.tripId);
      } else if (detail?.tab) switchTab(detail.tab as TabId);
    };
    window.addEventListener('nav-tab', handler);
    return () => window.removeEventListener('nav-tab', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!importNavigation || activeTrip?.id !== importNavigation.tripId || !branchPersistence || branchPersistence.loading) return;
    const scope = importNavigation.branchId ? branchPersistence.configs[importNavigation.branchId] : activeTrip;
    if (importNavigation.itemId && scope && !Object.values(scope.itinerary ?? {}).some(day =>
      [day.attractions, day.eats, day.nightlife, day.shopping, day.notes].some(items =>
        items?.some(item => item.id === importNavigation.itemId)))) return;
    // Select only after the new root's workspace is ready; selecting against
    // the old trip would lose the branch on a cross-trip import.
    branchPersistence.select(importNavigation.branchId || null);
    if (importNavigation.tab === 'bucketlist') dismissIntro('bucketlist');
    switchTab(importNavigation.tab);
    openDeepLink(importNavigation);
    setImportNavigation(null);
  }, [importNavigation, activeTrip, branchPersistence]);

  const updateActiveTrip = async (config: TripConfig): Promise<boolean> => {
    if (!currentUser) return false;
    // Private branch metadata only changes through the server-owned workspace.
    if (config.branchId || config.id.includes('/')) return false;

    // Recursively strip undefined values — Firestore rejects writes containing
    // any undefined value (must be null, missing, or a real value). Optional
    // User/Destination fields like photoURL, dob, blurb, country etc. are often
    // undefined and would silently fail the entire setDoc otherwise.
    //
    // IMPORTANT: only recurse into plain objects + arrays. Firebase FieldValue
    // sentinels (e.g. serverTimestamp()) are class instances — recursing into
    // them rebuilds them as plain objects, destroying the sentinel and causing
    // the server-side rule check `createdAt == request.time` to silently fail.
    // stripUndef (dayWrites) is the shared one, and carries exactly this guard.

    const stripConfig = (c: TripConfig) => {
      // Pull createdAt OUT before stripUndef so the FieldValue sentinel
      // can never be touched. Even though stripUndef gates on
      // the prototype, Firebase's bundled FieldValue class may not satisfy
      // that check in production builds, and any recursion into it would
      // copy out its enumerable props as a plain object, destroying the
      // sentinel and saving `{ _methodName: 'serverTimestamp' }` literally
      // (which then fails the `data.createdAt is timestamp` rule check).
      const { expenses, payments, memories, itinerary, createdAt, ...stripped } = c;
      const result = stripUndef({ ...stripped,
        travellers: stripped.travellers.map(user => user.inviteStatus === 'pending'
          ? user : { ...publicTripTraveller(user), inviteStatus: user.inviteStatus }),
        destinations: withDestinationIds(stripped.destinations), status: getTripStatus(c) }) as Record<string, unknown>;
      if (createdAt !== undefined) {
        result.createdAt = createdAt; // re-attach the untouched sentinel
      }
      return result;
    };

    if (pendingTrip && config.id === pendingTrip.id) {
      if (config.title.trim() !== "") {
        // Firestore rule requires createdAt == request.time on CREATE.
        // serverTimestamp() resolves to request.time server-side, satisfying it.
        const configWithCreatedAt: TripConfig = { ...config, createdAt: serverTimestamp() };
        try {
          // Build the doc body completely manually so the serverTimestamp()
          // sentinel CANNOT be touched by stripUndef or anything else.
          // We compute a stripped body of everything EXCEPT createdAt, then
          // attach a freshly-minted serverTimestamp() at the very last step.
          const sentinel = serverTimestamp();
          const dataToSave: Record<string, unknown> = {
            ...stripConfig(config),  // config here has NO createdAt (we never added it)
            createdAt: sentinel,
          };
          await setDoc(doc(db, 'trips', config.id), dataToSave);
          // Keep the draft/form mounted until the parent document is saved.
          setTrips(prev => {
            if (prev.find(t => t.id === config.id)) return prev;
            return [...prev, configWithCreatedAt].sort(
              (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
            );
          });
          setActiveTripId(config.id);
          setPendingTrip(null);
          return true;
        } catch (err) {
          console.error("Error creating trip:", err);
          setPendingTrip(config);
          alert(`Couldn't save trip: ${userMessage(err, 'check your connection and try again.')}`);
          return false;
        }
      } else {
        setPendingTrip(config);
      }
      return false;
    }

    try {
      // A key-level merge was the old policy. It stopped a save deleting a
      // field it never mentioned — `inviteEmails` used to go that way, which
      // stranded an invitee on a notification whose Accept could only fail —
      // and it did nothing for the fields every save always mentions. Each
      // editor builds `{ ...tripConfig, destinations: next }`, so a title edit
      // carries a full destinations array too, and two admins editing two
      // different destinations at once both sent one: whoever landed second
      // silently erased the other's work.
      //
      // Now the save writes only what this editor actually changed, and merges
      // destinations/travellers/accommodations by identity when both clients
      // changed the same array (writeTripConfig). The baseline is the editor's
      // own view of the trip, which is what it spread — taken from the live
      // trip state here rather than threaded through all ten call sites.
      const previous = configBaseline(trips, config.id);
      if (!previous) {
        // No local copy to diff against, so there is no safe merge to make:
        // keep the old whole-body write rather than guessing at a baseline.
        await setDoc(doc(db, 'trips', config.id), stripConfig(config), { merge: true });
        return true;
      }
      await writeTripConfig(db, config.id, stripConfig(previous), stripConfig(config),
        (tripId, before, after) => changeTripRoute({ tripId, action: 'edit', before, after }));
      // The trip listener supplies saved state, including loaded subcollections.
      return true;
    } catch (err) {
      console.error("Error updating trip:", err);
      alert(`Couldn't save trip: ${userMessage(err, 'check your connection and try again.')}`);
      return false;
    }
  };

  const deleteTrip = async (tripId: string) => {
    if (!currentUser) return;
    try {
      // Immediate UI update: clear selection if this is the active trip
      if (activeTripId === tripId) {
        setActiveTripId("");
      }
      if (pendingTrip && pendingTrip.id === tripId) {
        setPendingTrip(null);
      }

      // Also remove from local list immediately to avoid flicker before Firestore syncs
      setTrips(prev => prev.filter(t => t.id !== tripId));

      // Same cascade as account deletion: subcollections, schedules, memory
      // photos, booking documents, then the trip itself.
      await deleteTripFully(tripId, currentUser.id);
    } catch (err) {
      console.error("Error deleting trip:", err);
      alert("Failed to delete trip. Please try again.");
    }
  };

  const addNewTrip = () => {
    if (!currentUser) return;
    // The LIVE auth uid, not currentUser.id. Every rule compares against
    // request.auth.uid, so a trip seeded from the profile id is one the
    // creator cannot write to if the two ever differ — the create itself is
    // refused, and any later write to it is a permission error that the
    // client, reading its own draft, reports as "you are the owner".
    // ImportItineraryModal already does this for the same reason.
    const authUid = auth.currentUser?.uid || currentUser.id;

    const homeLocation = currentUser.homeLocation || '';
    const homeCountry = homeLocation.split(',').pop()?.trim() || '';
    const baseCurrency = getCurrencyFromLocation(homeLocation);

    const newTrip: TripConfig = {
      ...INITIAL_TRIP_CONFIG,
      id: `trip-${Date.now()}`,
      title: "",
      travellers: [publicTripTraveller({ ...currentUser, id: authUid })],
      adminIds: [authUid],
      ownerId: authUid,
      memberIds: [authUid],
      homeLocation,
      homeCountry,
      baseCurrency,
      destinations: []
    };
    setPendingTrip(newTrip);
    setAutoOpenCreate(true);  // explicit signal — form only opens on user action
    setTab("logistics");
  };

  return (
    <PersonalRouteContext.Provider value={homeRoute?.visits}>
    <JunctionContext.Provider value={junctions}>
    <AnimatePresence mode="wait">
      {authLoading || !openingSplashElapsed ? (
        <motion.div 
          key="loading"
          initial={{ opacity: resumingUpdate ? 1 : 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="brand-loading-surface min-h-screen flex flex-col items-center justify-center p-8 text-center text-balance"
        >
          <div className="auth-blobs"><CardTintStreaks /></div>
          <ModernCompass size={80} />
          {!resumingUpdate && <div className={brandFonts === 'fallback' ? 'opening-brand-fallback' : undefined}
            style={{ visibility: brandFonts === 'pending' ? 'hidden' : 'visible' }}>
          <h1 className="mt-5 font-serif text-3xl font-semibold leading-none text-app-text">Talisman</h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="brand-tagline mt-2"
          >
            Every traveller’s lucky charm
          </motion.p>
          </div>}
        </motion.div>
      ) : !isFullyOnboarded ? (
        <motion.div
          key="auth"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="min-h-screen"
        >
          <AuthScreen 
            onSuccess={() => {
              setTab("home");
            }} 
          />
        </motion.div>
      ) : (
        <motion.div 
          key="app"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="h-full bg-app-bg font-sans selection:bg-gold/10"
        >
      <div className="max-w-md mx-auto h-full flex flex-col bg-app-bg relative">

        {/* ── Pinned top zone: header + sub-header together ──────────────────
             Wrapped in a single sticky+shrink-0 container so they pin as a
             unit. Works whether <main> or the page is the scroll container. */}
        {/* Bone on every tab. This strip is the one fixed thing in the app, so
             it holds the brand colour whatever the page below it is doing —
             tinted tabs push their own white no further up than <main>. */}
        <div className="sticky top-0 z-50 shrink-0 bg-app-chrome">
        {/* Header */}
        <header className="bg-app-chrome border-b border-app-border/40 px-6 py-2">
          <div className="flex items-center justify-between">
            <h1><TalismanLogo /></h1>
            <div className="flex items-center gap-2">
              {/* Bell — standalone, left of avatar */}
              <div className="relative">
                <button
                  onClick={() => { setShowNotifications(true); setShowSettings(false); }}
                  aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
                  className="tap-target w-9 h-9 rounded-full flex items-center justify-center bg-app-surface border border-app-border/40 shadow-[0_2px_8px_-2px_rgba(28,28,28,0.18)] transition hover:bg-app-surface-up hover:shadow-[0_3px_10px_-2px_rgba(28,28,28,0.24)] active:scale-95 relative outline-none"
                >
                  <Bell className="w-4 h-4 text-gold" />
                  {unreadCount > 0 && (
                    <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-rose-500 rounded-full border-2 border-white shadow-sm" />
                  )}
                </button>
              </div>

              {/* Profile avatar → settings dropdown */}
              <div
                className="relative"
                ref={settingsRef}
              >
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  aria-label="Account menu"
                  aria-expanded={showSettings}
                  className="tap-target w-9 h-9 rounded-full flex items-center justify-center transition hover:opacity-90 active:scale-95 relative outline-none"
                >
                  <UserAvatar userId={currentUser.id} size={36} noTooltip />
                </button>

                <AnimatePresence>
                  {showSettings && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: 10 }}
                      className="absolute top-full right-0 mt-3 w-56 bg-white border border-app-border shadow-2xl shadow-black/20 rounded-2xl p-1.5 z-[100]"
                    >
                      <div className="px-4 py-3 border-b border-app-border/50 mb-1 flex items-center gap-3">
                        <UserAvatar userId={currentUser.id} size={32} />
                        <div>
                          <div className="text-[10px] font-black uppercase tracking-widest text-app-sub/60">Account</div>
                          <div className="text-sm font-bold text-app-text">{getFirstName(currentUser.name)}</div>
                        </div>
                      </div>

                      <button
                        onClick={() => { setShowSettingsModal(true); setShowSettings(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-app-faint transition-colors text-left group/item focus:outline-none"
                      >
                        <div className="w-8 h-8 rounded-lg bg-app-faint flex items-center justify-center group-hover/item:bg-gold/10 transition-colors">
                          <Settings className="w-4 h-4 text-app-sub group-hover/item:text-gold" />
                        </div>
                        <span className="text-xs font-semibold text-app-sub group-hover/item:text-app-text">Account Settings</span>
                      </button>

                      <div className="mt-1 pt-1 border-t border-app-border/50">
                        <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-rose-50 transition-colors text-left group/item font-medium text-rose-500 focus:outline-none" onClick={(e) => { e.stopPropagation(); signOut(); }}>
                          <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center group-hover/item:bg-rose-100 transition-colors">
                            <LogOut className="w-4 h-4" />
                          </div>
                          <span className="text-xs font-bold">Log Out</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </header>

        {/* ── Sub-header: Log expenses / Settle toggle ────────────────────────────
             Inside the sticky wrapper above so it pins with the header. The
             Overview/Itinerary toggle that used to live here is now its own
             top-level nav tab — see the TABS array above. Hidden until a trip
             exists: with no trip both sides are empty, so the choice is
             meaningless and it just crowds the empty state.                  */}
        {tab === "expenses" && hasTrip && (
          <div className="bg-app-bg border-b border-app-border/30 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] px-6 pt-3 pb-3">
            <div className="flex bg-app-faint/50 p-1.5 rounded-2xl w-full border border-app-border/40">
              {[
                { id: 'log' as const, label: 'Log expenses', icon: <DollarSign className="w-4 h-4" /> },
                { id: 'settle' as const, label: 'Settle', icon: <Handshake className="w-4 h-4" /> },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setExpensesTab(t.id)}
                  className={`
                    flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-[11px] font-black tracking-widest uppercase transition-all
                    ${expensesTab === t.id ? 'bg-white text-app-text shadow-sm border border-black/5 ring-1 ring-black/5' : 'text-app-sub hover:text-app-text'}
                  `}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        )}

        </div>{/* end sticky top zone */}

        {/* Content Area */}
        <div id="import-modal-root" className="flex-1 relative overflow-hidden">
        {/* Behind <main>, inside its own clipping box. The box can't be this
            wrapper: portal modals mount here and position themselves `fixed`,
            and `contain: paint` would make the wrapper their containing block
            — the iOS full-screen-modal trap all over again. */}
        {tintedSurface && (
          <div className="tint-backdrop absolute inset-0 overflow-hidden" style={{ contain: 'paint' }} aria-hidden="true">
            <CardTintStreaks />
          </div>
        )}
        <main
          ref={scrollRef}
          data-surface={tintedSurface ? 'tinted' : undefined}
          className={`relative z-10 h-full overflow-x-clip px-6 no-scrollbar ${
            tabOwnsScroller
              ? 'pt-0 overflow-hidden' // Both own their own inner scroller (see TripOverview / the
                                       // dayPlanner branch in ItineraryTab) so their fixed header
                                       // (trip name / date strip) sits outside the bouncing scroll
                                       // area entirely — a plain `position: sticky` header turned
                                       // out to only stay stuck within its own immediate parent's
                                       // height, which for the date strip was barely taller than the
                                       // strip itself, so it just scrolled away with everything else.
                                       // A genuine flex-sibling split doesn't have that limitation.
              : hasTrip && isBucketListView && !showingIntro
              ? 'pt-0 overflow-y-auto pb-[6.5rem]' // Shortlist's sticky section headers
                                                    // (AccordionSection stickyHeader) DO stay stuck
                                                    // via plain position:sticky (each section's own
                                                    // wrapper is tall enough — no restructuring
                                                    // needed there), but <main>'s own top padding
                                                    // sat *outside* the sticky element's opaque
                                                    // background, leaving an unpainted gap for
                                                    // scrolled-past content to peek through above
                                                    // whichever section is currently pinned. Zeroed
                                                    // here; AccordionSection's sticky wrapper owns
                                                    // the equivalent padding itself instead.
              : 'pt-4 overflow-y-auto pb-[6.5rem]'
          }`}
        >
          <div className={`animate-fade-up flex flex-col ${tabOwnsScroller ? 'h-full' : 'min-h-full'}`}>
            <ErrorBoundary>
            <Suspense fallback={null}>
              {tab === "home" && (
                <HomeTab
                  user={currentUser}
                  tripConfig={activeTrip}
                  route={homeRoute}
                  routeError={branchPersistence?.error}
                  onOpenScope={id=>{branchPersistence?.select(id);switchTab('logistics');}}
                  expenseSummary={expenseSummary}
                  hasSavedTrips={trips.length > 0}
                  onAddTrip={addNewTrip}
                  onUpdateConfig={updateActiveTrip}
                  onNavigate={id=>{if(id==='expenses')branchPersistence?.select(null);switchTab(id);}}
                />
              )}
              {(tab === "logistics" || tab === "bucketlist" || tab === "dayplanner") && (
                // The intro gate sits out here rather than inside ItineraryTab
                // on purpose: that component's two branches must return the
                // same root element type, or the keyed import modal remounts
                // and an in-progress review is lost (itineraryImportHost.test.ts).
                // Wrapping its return broke exactly that.
                <IntroGate
                  showIntro={
                    !!hasTrip && (
                      (tab === 'bucketlist' && !introSeen.bucketlist) ||
                      (tab === 'dayplanner' && !introSeen.dayplanner)
                    )
                  }
                  // flex-1/min-h-0 rather than h-full: this sits between
                  // <main>'s column and the tab, and a percentage height on a
                  // flex child is the fragile way to fill a parent whose own
                  // height is itself derived.
                  className="flex-1 min-h-0 flex flex-col"
                  intro={
                    <EmptyState
                      icon={TAB_INTRO_COPY[tab === 'dayplanner' ? 'dayplanner' : 'bucketlist'].icon}
                      label={TAB_LABELS[tab === 'dayplanner' ? 'dayplanner' : 'bucketlist']}
                      title={TAB_INTRO_COPY[tab === 'dayplanner' ? 'dayplanner' : 'bucketlist'].title}
                      body={TAB_INTRO_COPY[tab === 'dayplanner' ? 'dayplanner' : 'bucketlist'].body}
                      actionLabel="Get started"
                      onAction={() => dismissIntro(tab === 'dayplanner' ? 'dayplanner' : 'bucketlist')}
                    />
                  }
                >
                <ItineraryTab
                  onAddTrip={addNewTrip}
                  onTripCreated={(tripId) => { setActiveTripId(tripId); }}
                  trips={trips}
                  activeTripId={activeTripId}
                  onSelectTrip={setActiveTripId}
                  onDeleteTrip={deleteTrip}
                  user={currentUser}
                  tripConfig={activeTrip}
                  onUpdateConfig={updateActiveTrip}
                  initialManageState={triggerManage}
                  branchWorkspace={tab === 'logistics' ? branchWorkspace : undefined}
                  personalRoute={tab === 'bucketlist' || tab === 'dayplanner' ? homeRoute : undefined}
                  routeError={branchPersistence?.error}
                  view={itineraryView}
                  onViewChange={handleItineraryViewChange}
                  detailMode={itineraryDetailMode}
                  onDetailModeChange={handleItineraryDetailModeChange}
                  autoOpenCreate={autoOpenCreate}
                  onConsumeOpenCreate={() => setAutoOpenCreate(false)}
                  onCancelCreate={() => { setPendingTrip(null); setAutoOpenCreate(false); }}
                  onOpenChange={(open) => {
                    if (!open) {
                      setTriggerManage(false);
                      // If we were managing a pending trip and it's still missing a title, clear it
                      if (pendingTrip && pendingTrip.title.trim() === "") {
                        setPendingTrip(null);
                      }
                    }
                  }}
                />
                </IntroGate>
              )}
              {tab === "expenses" && (
                <ExpensesTab
                  key={activeTrip?.id ?? 'no-trip'}
                  user={currentUser}
                  hasTrip={hasTrip}
                  onAddTrip={addNewTrip}
                  tripConfig={activeTrip}
                  expenseScopes={expenseScopes}
                  summary={expenseSummary}
                  focusedScopeId={branchPersistence?.selected ? `${activeTrip?.id}/branches/${branchPersistence.selected}` : activeTrip?.id}
                  onSelectScope={scopeId => branchPersistence?.select(scopeId === activeTrip?.id ? null : scopeId.split('/branches/')[1])}
                  onUpdateConfig={updateActiveTrip}
                  onNavigate={switchTab}
                  tab={expensesTab}
                  onTabChange={setExpensesTab}
                  showIntro={!!hasTrip && !introSeen.expenses}
                  onIntroDismiss={() => dismissIntro('expenses')}
                />
              )}
              {tab === "memories" && (
                <MemoriesTab 
                  user={currentUser} 
                  hasTrip={hasTrip} 
                  onAddTrip={addNewTrip} 
                  tripConfig={activeTrip}
                  onUpdateConfig={updateActiveTrip}
                />
              )}
            </Suspense>
            </ErrorBoundary>
          </div>
        </main>
        </div>

        {/* Bottom Navigation */}
        {/* Suspense sits outside AnimatePresence on purpose: AnimatePresence
            tracks its direct children to run exit animations, so a boundary
            in between would break the modal's close animation. */}
        <Suspense fallback={null}>
          {editingLinkedExpense && linkedExpenseTrip && currentUser
            && (editingLinkedExpense.paid_by === currentUser.id || isTripAdmin(linkedExpenseTrip, currentUser.id)) && (
            <ExpensesTab key={`linked-edit-${linkedExpenseTrip.id}-${editingLinkedExpense.id}`}
              user={currentUser} hasTrip onAddTrip={addNewTrip} tripConfig={linkedExpenseTrip}
              onUpdateConfig={updateActiveTrip} tab="log" onTabChange={() => {}}
              portalEdit={{ expense: editingLinkedExpense, onClose: () => setLinkedExpenseEditId(null) }} />
          )}
          {linkedExpense && linkedExpenseTrip && currentUser && (
            <LinkedExpenseItem
              key={linkedExpense.id}
              expense={linkedExpense}
              travellers={linkedExpenseTrip.travellers || []}
              currentUser={currentUser}
              isAdmin={isTripAdmin(linkedExpenseTrip, currentUser.id)}
              onEdit={expense => { setLinkedExpenseId(null); setLinkedExpenseEditId(expense.id); }}
              destinations={linkedExpenseTrip.destinations}
              portalOnly
              onPortalClose={() => setLinkedExpenseId(null)}
            />
          )}
        <AnimatePresence>
          {showSettingsModal && (
            <SettingsModal
              user={currentUser}
              onClose={() => setShowSettingsModal(false)}
              onUpdate={() => {
                // The update is already saved in Firestore and AuthContext refreshes it
              }}
            />
          )}
          {pendingImportToOpen && pendingImportTripConfig && (
            <ImportBookingModal
              tripConfig={pendingImportTripConfig}
              allTrips={trips}
              itineraryDays={[]}
              currentUser={currentUser}
              onUpdateConfig={updateActiveTrip}
              onClose={() => setPendingImportToOpen(null)}
              pendingImport={pendingImportToOpen}
              onMarkReviewed={markImportReviewed}
              tripConfirmed={pendingImportTripConfirmed}
            />
          )}
        </AnimatePresence>
        </Suspense>
        {/* Outside the AnimatePresence above: NotificationCenter is now a
            createPortal modal, and AnimatePresence cannot track a child that
            renders into a different tree — it never unmounts it. Plain
            conditional render instead (no exit animation, reliable close). */}
        {showNotifications && (
          <NotificationCenter
            userId={currentUser.id}
            notifications={notifications}
            trips={trips}
            activeTripId={activeTripId}
            onSwitchTrip={(id) => { setActiveTripId(id); }}
            onClose={() => setShowNotifications(false)}
            onOpenImport={handleOpenImport}
            pendingImportIds={pendingImportIds}
            onNavigate={(tab, branchId) => { if(tab==='expenses' || branchId)branchPersistence?.select(branchId ?? null);switchTab(tab as any); setShowNotifications(false); }}
          />
        )}
        {/* ── Offline toast ───────────────────────────────────────────────── */}
        <AnimatePresence>
          {!isOnline && (
            <motion.div
              key="offline-toast"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="fixed bottom-[112px] left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full bg-neutral-700/90 backdrop-blur-sm shadow-lg pointer-events-none"
            >
              <WifiOff className="w-3.5 h-3.5 text-neutral-300 shrink-0" />
              <p className="text-[11px] font-semibold text-neutral-100 whitespace-nowrap">
                You're offline. Changes will be saved once reconnected.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* No horizontal padding on <nav> itself — with 5 equal-content tabs,
             justify-evenly below already puts identical spacing between every
             pair (including each edge and its nearest tab); adding padding on
             top of that pushed tabs #1/#5 in further than the gaps between
             tabs #1–#4/#2–#5, since it stacked on top of an already-even
             distribution instead of being absorbed into it. */}
        <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-app-bg border-t border-app-border py-2 pb-6 z-50">
          <div className="flex items-start justify-evenly relative">
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => switchTab(t.id)}
                  className="flex flex-col items-center gap-1 transition outline-none"
                >
                  <motion.div
                    data-nav-tab={t.id}
                    key={`${t.id}-${navArrivalPulse[t.id] || 0}`}
                    initial={(navArrivalPulse[t.id] || 0) > 0 ? { scale: 0.7 } : false}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 12 }}
                    className={`
                    w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300
                    ${active ? 'bg-gold/5 text-gold' : 'text-app-sub/40 hover:text-app-text'}
                  `}>
                    <div className={`transition-transform duration-500 ${active ? 'scale-110' : 'hover:scale-110'}`}>
                      {t.icon}
                    </div>
                  </motion.div>
                  <span className={`
                    text-[9px] font-bold uppercase tracking-[0.12em] transition-all duration-300 text-center leading-tight
                    ${active ? 'text-gold opacity-100' : 'opacity-40'}
                  `}>
                    {t.label.split(' ').map((word, i) => (
                      <span key={i} className="block">{word}</span>
                    ))}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
        <ShootingStarLayer onArrive={(target) => setNavArrivalPulse(p => ({ ...p, [target]: (p[target] || 0) + 1 }))} />
      </div>
    </motion.div>
  )}
</AnimatePresence>
    </JunctionContext.Provider>
    </PersonalRouteContext.Provider>
  );
}

// Fired by things like the Shortlist "add to Schedule" toggle and the Pick-a-
// spot picker (via `window.dispatchEvent(new CustomEvent('schedule-flight',
// { detail: {x,y,target} }))`) — a little comet/spark flies from the
// button's on-screen position to the named nav tab (`target`, defaulting to
// the Schedule tab; looked up live via `[data-nav-tab="<target>"]`, so this
// component needs no prop-drilled ref from three components away), then that
// nav icon does a quick spring "pop" to sell the arrival.
type Flight = { id: number; from: { x: number; y: number }; to: { x: number; y: number }; target: string };

function ShootingStarLayer({ onArrive }: { onArrive: (target: string) => void }) {
  const [flights, setFlights] = useState<Flight[]>([]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { x: number; y: number; target?: string };
      const target = detail.target || 'dayplanner';
      const targetEl = document.querySelector(`[data-nav-tab="${target}"]`);
      if (!targetEl) return;
      const r = targetEl.getBoundingClientRect();
      const to = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      const id = Date.now() + Math.random();
      setFlights(f => [...f, { id, from: { x: detail.x, y: detail.y }, to, target }]);
    };
    window.addEventListener('schedule-flight', handler);
    return () => window.removeEventListener('schedule-flight', handler);
  }, []);

  if (flights.length === 0) return null;

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[300]">
      <AnimatePresence>
        {flights.map(f => (
          <div key={f.id}>
            <ShootingStar
              from={f.from}
              to={f.to}
              onDone={() => {
                setFlights(cur => cur.filter(fl => fl.id !== f.id));
                onArrive(f.target);
              }}
            />
          </div>
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
}

function ShootingStar({ from, to, onDone }: { from: { x: number; y: number }; to: { x: number; y: number }; onDone: () => void }) {
  // Arcs upward through the midpoint rather than a straight line — reads
  // more like a thrown/shot object than a simple slide.
  const midX = (from.x + to.x) / 2;
  const midY = Math.min(from.y, to.y) - 70;
  const path = { left: [from.x, midX, to.x], top: [from.y, midY, to.y] };

  return (
    <>
      {/* Trailing echo — same path, slightly delayed/smaller/fainter, faking a comet tail. */}
      <motion.div
        initial={{ ...path, left: from.x, top: from.y, opacity: 0, scale: 0.5 }}
        animate={{ ...path, opacity: [0, 0.5, 0], scale: [0.5, 0.7, 0.2] }}
        transition={{ duration: 1.1, delay: 0.1, ease: 'easeInOut', times: [0, 0.6, 1] }}
        className="absolute -ml-1.5 -mt-1.5 w-3 h-3 rounded-full bg-gold/50 blur-[2px]"
      />
      <motion.div
        initial={{ ...path, left: from.x, top: from.y, opacity: 0, scale: 0.5 }}
        animate={{ ...path, opacity: [0, 1, 0], scale: [0.5, 1.1, 0.3] }}
        transition={{ duration: 1.1, ease: 'easeInOut', times: [0, 0.6, 1] }}
        onAnimationComplete={onDone}
        className="absolute -ml-2.5 -mt-2.5 w-5 h-5 rounded-full bg-gold flex items-center justify-center shadow-[0_0_14px_4px_rgba(197,160,89,0.75)]"
      >
        <Sparkles className="w-3 h-3 text-white" />
      </motion.div>
    </>
  );
}
